import { motion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

export const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay, ease: EASE }}
    className={className}
  >
    {children}
  </motion.div>
);

const Frame = ({ children, className = "", style }) => (
  <div
    className={`rounded-2xl bg-white border border-slate-200/80 shadow-[0_24px_60px_-24px_rgba(76,29,149,0.28)] ${className}`}
    style={style}
  >
    {children}
  </div>
);

export const StockMateInvoice = ({ className = "", style }) => (
  <Frame className={`w-64 p-4 space-y-3 ${className}`} style={style}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-sm bg-primary" />
        <span className="text-[11px] font-bold tracking-widest text-slate-800">STOCKMATE</span>
      </div>
      <span className="text-[9px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary">
        NEW INVOICE
      </span>
    </div>
    <div className="space-y-2">
      {[
        ["Basmati Rice 1kg", "×2", "₹240"],
        ["Sunflower Oil 1L", "×1", "₹145"],
        ["Parle-G (pack)", "×4", "₹40"],
      ].map(([name, qty, price]) => (
        <div key={name} className="flex items-center justify-between text-[11px]">
          <span className="text-slate-700">
            {name} <span className="text-slate-400">{qty}</span>
          </span>
          <span className="font-semibold text-slate-900">{price}</span>
        </div>
      ))}
    </div>
    <div className="border-t border-dashed border-slate-200 pt-2 flex items-center justify-between">
      <span className="text-[11px] text-slate-500">Total</span>
      <span className="text-sm font-bold text-slate-900">₹425</span>
    </div>
    <div className="flex items-center justify-between">
      <span className="text-[10px] text-slate-400">#INV-1042</span>
      <span className="text-[9px] font-bold px-2 py-1 rounded-md bg-emerald-100 text-emerald-700">
        PAID
      </span>
    </div>
  </Frame>
);

export const StockMateLedger = ({ className = "", style }) => (
  <Frame className={`w-56 p-4 space-y-3 ${className}`} style={style}>
    <div className="flex items-center justify-between">
      <span className="text-[10px] font-bold tracking-widest text-slate-800">LEDGER</span>
      <span className="text-[10px] text-slate-400">Rahim U.</span>
    </div>
    <div className="space-y-2">
      {[
        ["Rice 1kg ×1", "₹49"],
        ["Oil 500ml ×1", "₹49"],
        ["Biscuits ×2", "₹20"],
      ].map(([item, price]) => (
        <div key={item} className="flex items-center justify-between text-[11px]">
          <span className="text-slate-700">{item}</span>
          <span className="font-semibold text-slate-900">{price}</span>
        </div>
      ))}
    </div>
    <div className="border-t border-dashed border-slate-200 pt-2 flex items-center justify-between">
      <span className="text-[10px] text-slate-500">Balance</span>
      <span className="text-[9px] font-bold px-2 py-1 rounded-md bg-amber-100 text-amber-700">
        ₹118 UDHAR
      </span>
    </div>
  </Frame>
);

export const StorepilotDash = ({ className = "", style }) => (
  <Frame className={`w-64 p-4 space-y-4 ${className}`} style={style}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-sm bg-primary" />
        <span className="text-[11px] font-bold tracking-widest text-slate-800">STOREPILOT</span>
      </div>
      <span className="flex items-center gap-1 text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
        <span className="w-1 h-1 rounded-full bg-emerald-500" />
        SYNCED
      </span>
    </div>
    <div>
      <p className="text-[10px] text-slate-400 uppercase tracking-wider">Today's sales</p>
      <p className="text-2xl font-bold text-slate-900">₹4,230</p>
    </div>
    <div className="flex items-end gap-1 h-14">
      {[35, 55, 40, 70, 50, 85, 60, 95].map((h, i) => (
        <span
          key={i}
          className="flex-1 rounded-sm bg-primary/80"
          style={{ height: `${h}%`, opacity: 0.35 + (h / 100) * 0.65 }}
        />
      ))}
    </div>
    <div className="flex gap-1.5">
      {["QR PAYMENTS", "LEDGER", "CLOUD SYNC"].map((c) => (
        <span key={c} className="text-[8px] font-semibold px-2 py-1 rounded-md border border-slate-200 text-slate-500">
          {c}
        </span>
      ))}
    </div>
  </Frame>
);

export const AiChat = ({ className = "", style }) => (
  <Frame className={`w-72 p-4 space-y-3 ${className}`} style={style}>
    <span className="text-[10px] font-bold tracking-widest text-slate-800">AI WEBSITE BUILDER</span>
    <div className="flex justify-end">
      <p className="text-[11px] leading-relaxed bg-primary text-white rounded-xl rounded-br-sm px-3 py-2 max-w-[85%]">
        A website for my bakery — menu, photos and online orders.
      </p>
    </div>
    <div className="flex justify-start">
      <p className="text-[11px] leading-relaxed bg-slate-100 text-slate-700 rounded-xl rounded-bl-sm px-3 py-2 max-w-[85%]">
        Got it. Generating structure — Home · Menu · Gallery · Order…
      </p>
    </div>
  </Frame>
);

export const BrowserSite = ({ className = "", style }) => (
  <Frame className={`w-72 overflow-hidden ${className}`} style={style}>
    <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
      <span className="flex gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
        <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
        <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
      </span>
      <span className="flex-1 text-[9px] text-slate-400 bg-slate-100 rounded-full px-3 py-1">
        misbahbakery.in
      </span>
    </div>
    <div className="p-3 space-y-2.5">
      <div className="flex items-center justify-between">
        <span className="w-8 h-1.5 rounded-full bg-slate-800" />
        <span className="flex gap-1.5">
          <span className="w-5 h-1 rounded-full bg-slate-200" />
          <span className="w-5 h-1 rounded-full bg-slate-200" />
          <span className="w-5 h-1 rounded-full bg-slate-200" />
        </span>
      </div>
      <div className="rounded-lg bg-primary/90 h-16 p-2.5 space-y-1.5">
        <span className="block w-20 h-1.5 rounded-full bg-white/90" />
        <span className="block w-14 h-1 rounded-full bg-white/50" />
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        <span className="h-8 rounded-md bg-slate-200" />
        <span className="h-8 rounded-md bg-slate-200" />
        <span className="h-8 rounded-md bg-slate-200" />
      </div>
      <span className="block w-16 h-1 rounded-full bg-slate-200" />
    </div>
  </Frame>
);
